import React from 'react';
import { Select } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';
import BaseInputLabel from '../base-input-label';

import type { IBaseSelectBoxProps } from './types';

const BaseSelectBox: React.FC<IBaseSelectBoxProps> = (props) => {
  const { t } = useTranslation();

  const {
    value,
    placeholder = t('controls.select'),
    disabled,
    plaintext,
    readonly,
    invalid,
    onChange,
    className,
    style,
    allowClear = false,
    allowSearch = false,
    mode,
    maxWidth,
    minWidth,
    defaultValue,
    prefix,
    icon,
    labelRender,
    options = [],
    dataCy = 'base-select-box',
    size,
    onDeselect,
    onClear,
    maxTagTextLength,
    maxTagCount,
    loading,
    label,
    filterOption,
    open,
    onOpenChange,
    useUndefinedValue = false,
    getPopupContainer,
  } = props;

  const selectBoxClassNames = classNames(
    s.select,
    {
      [s.readonly]: readonly,
    },
    className,
  );

  const selectValue = defaultValue ? undefined : value ?? null;

  const Component = (
    <div data-cy={dataCy}>
      <Select
        loading={loading}
        style={{
          width: '100%',
          minWidth,
          maxWidth,
          ...style,
        }}
        // open
        options={options}
        mode={mode}
        value={useUndefinedValue ? undefined : selectValue}
        onChange={(val: any, option: any) => {
          if (onChange) onChange(val ?? null, option);
        }}
        disabled={disabled}
        placeholder={placeholder}
        className={selectBoxClassNames}
        variant={plaintext ? 'borderless' : 'outlined'}
        status={invalid ? 'error' : undefined}
        prefix={prefix}
        suffixIcon={disabled || readonly ? null : icon}
        allowClear={allowClear}
        labelRender={labelRender}
        defaultValue={defaultValue}
        optionRender={(option) => (
          <div data-cy="select-option">{option.label}</div>
        )}
        size={size}
        onClear={onClear}
        onDeselect={onDeselect}
        optionFilterProp="label"
        maxTagTextLength={maxTagTextLength}
        maxTagCount={maxTagCount}
        showSearch={allowSearch}
        filterOption={filterOption}
        open={open}
        onDropdownVisibleChange={onOpenChange}
        getPopupContainer={getPopupContainer}
      />
    </div>
  );

  return label ? (
    <BaseInputLabel {...label}>{Component}</BaseInputLabel>
  ) : (
    Component
  );
};

export const BaseSelectBoxDisplayName = 'BaseSelectBox';
BaseSelectBox.displayName = BaseSelectBoxDisplayName;

export default BaseSelectBox;
